import { useState } from "react";
import { toast } from "sonner";
import { Folder, FolderOpen, ListChecks, Loader2, Pencil } from "lucide-react";
import { appsApi, errMsg, notifyError, fmtDate, btnDark } from "@/lib/appsApi";

// Groups checklist lines by the folder they sit in; folder rules (ending in "/") go under their parent folder.
const groupByFolder = (patterns) => {
  const groups = new Map();
  patterns.forEach((p) => {
    const trimmed = p.endsWith("/") ? p.slice(0, -1) : p;
    const cut = trimmed.lastIndexOf("/");
    const dir = cut < 0 ? "" : `${trimmed.slice(0, cut)}/`;
    if (!groups.has(dir)) groups.set(dir, []);
    groups.get(dir).push(p.slice(dir.length));
  });
  return [...groups.entries()].sort(([a], [b]) => (a === "" ? -1 : b === "" ? 1 : a.localeCompare(b, undefined, { sensitivity: "base" })));
};

const GroupedList = ({ patterns }) => (
  <div className="mt-5 max-h-96 space-y-4 overflow-y-auto pr-1" data-testid="bundle-rules-list">
    {groupByFolder(patterns).map(([dir, items]) => (
      <div key={dir || "top"} data-testid={`bundle-rules-group-${dir || "top"}`}>
        <p className="flex items-center gap-1.5 font-mono text-xs font-semibold text-ink">
          {dir ? <Folder className="h-3.5 w-3.5 text-sky-600" /> : <FolderOpen className="h-3.5 w-3.5 text-sky-600" />}
          {dir || "Top of the zip"}<span className="font-normal text-slate-500">· {items.length}</span>
        </p>
        <ul className="mt-2 flex flex-wrap gap-2 border-l-2 border-sky-100 pl-3">
          {[...items].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base", numeric: true })).map((p) => <li key={p} className="rounded-full bg-[#F4F9FD] px-3 py-1 font-mono text-[11px] text-ink">{p}</li>)}
        </ul>
      </div>
    ))}
  </div>
);

export const BundleRules = ({ rules, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const patterns = Array.isArray(rules?.patterns) ? rules.patterns : [];

  const start = () => {
    setText(patterns.join("\n"));
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await appsApi.put("/admin/bundle-rules", { patterns: text.split(/\r?\n/) });
      onSaved(r?.data);
      setEditing(false);
      toast.success(`Checklist saved and sorted A–Z: ${r?.data?.patterns?.length ?? 0} item(s)`);
    } catch (err) {
      notifyError(errMsg(err, "Saving the bundle checklist"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-10 rounded-[28px] border border-sky-100 bg-white p-6 sm:p-8" data-testid="bundle-rules">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-700"><ListChecks className="h-5 w-5" /></span>
          <div>
            <h3 className="font-display text-xl font-semibold text-ink">Mission Ready Bundle checklist</h3>
            <p className="mt-1 text-sm text-slate-600" data-testid="bundle-rules-summary">
              {patterns.length} checklist item{patterns.length === 1 ? "" : "s"} · guide only, never blocks saving{rules?.updated_at ? ` · edited ${fmtDate(rules.updated_at)} by ${rules.updated_by}` : ""}
            </p>
          </div>
        </div>
        {!editing && <button onClick={start} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm text-ink hover:border-sky-600 hover:text-sky-700" data-testid="bundle-rules-edit"><Pencil className="h-4 w-4" />Edit</button>}
      </div>
      {editing ? (
        <div className="mt-5">
          <p className="text-xs leading-relaxed text-slate-600">One per line, relative to the top of the zip. <code>*</code> matches any characters in a name (for hashed files like <code>main.*.js</code>), <code>**</code> matches across folders, and a line ending in <code>/</code> means that folder must exist.</p>
          <textarea rows={14} value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 font-mono text-xs text-ink outline-none focus:border-sky-600" data-testid="bundle-rules-textarea" />
          <div className="mt-3 flex flex-wrap gap-3">
            <button onClick={save} disabled={busy} className={btnDark} data-testid="bundle-rules-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save checklist</button>
            <button onClick={() => setEditing(false)} disabled={busy} className="rounded-full px-5 py-3 text-sm text-slate-600 hover:text-ink" data-testid="bundle-rules-cancel">Cancel</button>
          </div>
        </div>
      ) : (
        <GroupedList patterns={patterns} />
      )}
    </section>
  );
};
